// src/pages/Achats/achatsUtils.js

export function parsePrice(value) {
  if (value == null || value === "") return 0;

  const normalized = String(value).replace(",", ".").replace(/[^\d.-]/g, "");
  const number = Number(normalized);

  return Number.isNaN(number) ? 0 : number;
}

export function formatMoney(value) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(parsePrice(value));
}

export function normalizeText(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
}

export function toAchatDate(value) {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value).trim();
  const normalized = raw.includes(" ") ? raw.replace(" ", "T") : raw;
  const date = new Date(normalized);

  return Number.isNaN(date.getTime()) ? null : date;
}

export function toDateOnly(value) {
  const date = toAchatDate(value);
  if (!date) return null;

  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function formatDateTimeFR(value) {
  const date = toAchatDate(value);
  if (!date) return "—";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function formatDateShort(value) {
  const date = toAchatDate(value);
  if (!date) return "—";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function getBuyer(achat) {
  return achat?.User || {};
}

export function getBuyerName(achat) {
  const user = getBuyer(achat);
  const fullName = `${user.prenom || ""} ${user.nom || ""}`.trim();

  return fullName || "Utilisateur inconnu";
}

export function isPaidPurchase(achat) {
  const stripe = normalizeText(achat?.payment_status);
  const statut = normalizeText(achat?.statut_reglement);

  return (
    stripe === "paid" ||
    stripe === "succeeded" ||
    statut.includes("regl") ||
    statut.includes("paye") ||
    statut.includes("valide")
  );
}

export function getFormulaLabel(value) {
  return value || "Formule non renseignée";
}

export function getFormulaTone(value) {
  const normalized = normalizeText(value);

  if (normalized.includes("ultim")) {
    return "bg-violet-100 text-violet-700 dark:bg-violet-950/70 dark:text-violet-300";
  }

  if (normalized.includes("pro")) {
    return "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/70 dark:text-indigo-300";
  }

  if (normalized.includes("free")) {
    return "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/70 dark:text-emerald-300";
  }

  if (normalized.includes("demo")) {
    return "bg-amber-100 text-amber-700 dark:bg-amber-950/70 dark:text-amber-300";
  }

  return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200";
}

export function getPaymentStatusLabel(achat) {
  if (achat?.payment_status) return achat.payment_status;
  if (isPaidPurchase(achat)) return "paid";

  return "—";
}

export function getReglementLabel(achat) {
  if (achat?.statut_reglement) return achat.statut_reglement;
  return isPaidPurchase(achat) ? "Réglé" : "Non réglé";
}

function csvCell(value) {
  const text = String(value ?? "").replace(/\r?\n|\r/g, " ");
  return `"${text.replace(/"/g, '""')}"`;
}

export function buildAchatsCSV(achats = []) {
  const headers = [
    "id",
    "date_achat",
    "nom",
    "prenom",
    "email",
    "formule",
    "prix_total",
    "statut_reglement",
    "payment_status",
    "user_achat_id",
  ];

  const rows = achats.map((achat) => {
    const user = getBuyer(achat);

    return [
      achat.id,
      toAchatDate(achat.date_achat)?.toISOString() || "",
      user.nom || "",
      user.prenom || "",
      user.email || "",
      achat.formule || "",
      String(parsePrice(achat.prix_total)),
      achat.statut_reglement || "",
      achat.payment_status || "",
      achat.user_achat_id || "",
    ];
  });

  return [
    headers.map(csvCell).join(";"),
    ...rows.map((row) => row.map(csvCell).join(";")),
  ].join("\n");
}

export function downloadCSV(filename, content) {
  const blob = new Blob(["\uFEFF" + content], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.click();

  URL.revokeObjectURL(url);
}

export function buildRevenue12Months(achats = []) {
  const buckets = [];
  const now = new Date();

  for (let index = 11; index >= 0; index -= 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - index, 1);

    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}`;

    buckets.push({
      key,
      label: `${String(date.getMonth() + 1).padStart(2, "0")}/${String(
        date.getFullYear()
      ).slice(-2)}`,
      value: 0,
      count: 0,
    });
  }

  const map = Object.fromEntries(buckets.map((bucket) => [bucket.key, bucket]));

  for (const achat of achats) {
    const date = toAchatDate(achat.date_achat);
    if (!date) continue;

    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}`;

    if (map[key]) {
      map[key].value += parsePrice(achat.prix_total);
      map[key].count += 1;
    }
  }

  return buckets;
}